import React, { useEffect, useMemo, useState } from 'react';
import { Form, Table } from 'react-bootstrap';
import Icon from '../../common/Icons';
import { EmptyBlock, formatNumber } from '../ui';
import { PHARMACY_COPY } from '../pharmacyCopy';
import { formatReportDate, formatReportMoney, formatReportUnitPrice } from '../utils/reportFormat';

const copy = PHARMACY_COPY.REPORTS;
const PAGE_SIZES = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 100;

function groupPrescriptions(lines = [], groups = []) {
  if (Array.isArray(groups) && groups.length > 0) {
    return [...groups].sort((a, b) => {
      const da = new Date(a.event_date || 0).getTime();
      const db = new Date(b.event_date || 0).getTime();
      if (db !== da) return db - da;
      return Number(b.dispensation_id) - Number(a.dispensation_id);
    });
  }

  const map = new Map();
  for (const line of lines) {
    const id = line.dispensation_id;
    if (!map.has(id)) {
      map.set(id, {
        dispensation_id: id,
        prescription_number: line.prescription_number,
        event_date: line.event_date,
        patient_display: line.patient_display,
        patient_matricule: line.patient_matricule,
        company_name: line.company_name,
        source_type: line.source_type,
        site_name: line.site_name,
        items: [],
        total_amount: 0,
        items_count: 0
      });
    }
    const g = map.get(id);
    g.items.push(line);
    g.items_count += 1;
    if (line.line_amount != null) g.total_amount += line.line_amount;
  }

  return [...map.values()].sort((a, b) => {
    const da = new Date(a.event_date || 0).getTime();
    const db = new Date(b.event_date || 0).getTime();
    if (db !== da) return db - da;
    return Number(b.dispensation_id) - Number(a.dispensation_id);
  });
}

export default function PharmacyReportPrescriptionsTable({
  lines = [],
  groups: groupsProp = [],
  avgValue = 0
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const groups = useMemo(() => groupPrescriptions(lines, groupsProp), [lines, groupsProp]);

  const totalPages = Math.max(1, Math.ceil(groups.length / pageSize));

  useEffect(() => {
    setPage(1);
  }, [groups.length, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return groups.slice(start, start + pageSize);
  }, [groups, page, pageSize]);

  const rangeFrom = groups.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, groups.length);

  if (groups.length === 0) {
    return <EmptyBlock label={copy.EMPTY_PRESCRIPTIONS} />;
  }

  return (
    <div className="cp-rx-report">
      <div className="cp-rx-report__toolbar">
        <div className="cp-rx-report__toolbar-meta">
          <span>
            {copy.PRESCRIPTIONS_AVG} : <strong>{formatReportMoney(avgValue)}</strong>
          </span>
          <span className="cp-rx-report__sep" aria-hidden>
            ·
          </span>
          <span>
            <strong>{formatNumber(groups.length)}</strong> {copy.PRESCRIPTIONS_COUNT}
          </span>
          <span className="cp-rx-report__sep" aria-hidden>
            ·
          </span>
          <span>
            <strong>{formatNumber(lines.length || groups.reduce((n, g) => n + (g.items?.length || 0), 0))}</strong>{' '}
            {copy.PRESCRIPTIONS_LINES}
          </span>
        </div>

        <div className="cp-rx-report__pager-controls">
          <label className="cp-rx-report__page-size">
            <span>{copy.PAGE_SIZE}</span>
            <Form.Select
              size="sm"
              value={String(pageSize)}
              onChange={(e) => setPageSize(Number(e.target.value))}
              aria-label={copy.PAGE_SIZE_ARIA}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Form.Select>
          </label>
        </div>
      </div>

      <div className="cp-table-wrap cp-rx-report__table-wrap">
        <Table className="cp-table cp-rx-report-table mb-0">
          <thead>
            <tr>
              <th style={{ width: '9rem' }}>{copy.COL_DATE}</th>
              <th style={{ width: '9rem' }}>{copy.COL_NUMBER}</th>
              <th>{copy.COL_PATIENT}</th>
              <th>{copy.COL_COMPANY}</th>
              <th>{copy.COL_MED}</th>
              <th className="text-end" style={{ width: '5rem' }}>
                {copy.COL_QTY}
              </th>
              <th className="text-end" style={{ width: '7.5rem' }}>
                {copy.COL_UNIT_PRICE}
              </th>
              <th className="text-end" style={{ width: '7.5rem' }}>
                {copy.COL_AMOUNT}
              </th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((rx) => {
              const items = rx.items || [];
              const rowSpan = Math.max(items.length, 1) + 1; // médicaments + ligne total
              const medRows = items.map((item, itemIdx) => {
                const isFirst = itemIdx === 0;
                return (
                  <tr
                    key={`${rx.dispensation_id}-${item.medication_id || itemIdx}`}
                    className={`cp-rx-report-table__row${isFirst ? ' is-rx-start' : ' is-rx-cont'}`}
                  >
                    {isFirst ? (
                      <>
                        <td className="cp-rx-report-table__date" rowSpan={rowSpan}>
                          <span className="cp-rx-report-table__date-value">{formatReportDate(rx.event_date)}</span>
                          {rx.source_type === 'PRESCRIPTION' ? (
                            <span className="cp-rx-report-table__badge">{copy.ORIGIN_RX}</span>
                          ) : (
                            <span className="cp-rx-report-table__badge cp-rx-report-table__badge--manual">
                              {copy.ORIGIN_MANUAL}
                            </span>
                          )}
                        </td>
                        <td className="cp-rx-report-table__number" rowSpan={rowSpan}>
                          <strong className="cp-rx-report-table__ref">{rx.prescription_number}</strong>
                          {rx.site_name && rx.site_name !== '—' ? (
                            <span className="cp-rx-report-table__site">{rx.site_name}</span>
                          ) : null}
                          <span className="cp-rx-report-table__meds-count">
                            {formatNumber(items.length)} {copy.MEDS_COUNT}
                          </span>
                        </td>
                        <td className="cp-rx-report-table__patient" rowSpan={rowSpan}>
                          <strong>
                            {rx.patient_first_name || rx.patient_last_name
                              ? [rx.patient_first_name, rx.patient_last_name].filter(Boolean).join(' ')
                              : (rx.patient_display || '—').replace(/\s·\s.*$/, '')}
                          </strong>
                          {rx.patient_matricule ? (
                            <span className="cp-rx-report-table__matricule">Mat. {rx.patient_matricule}</span>
                          ) : null}
                        </td>
                        <td className="cp-rx-report-table__company" rowSpan={rowSpan}>
                          {rx.company_name || '—'}
                        </td>
                      </>
                    ) : null}
                    <td className="cp-rx-report-table__med">
                      <span className="cp-rx-report-table__med-idx" aria-hidden>
                        {itemIdx + 1}.
                      </span>
                      <span className="cp-rx-report-table__med-body">
                        <strong>{item.medication_name}</strong>
                        {item.unit_name ? (
                          <span className="cp-rx-report-table__unit">{item.unit_name}</span>
                        ) : null}
                        {item.dosage_instructions ? (
                          <span className="cp-rx-report-table__dosage">{item.dosage_instructions}</span>
                        ) : null}
                      </span>
                    </td>
                    <td className="text-end cp-num">{formatNumber(item.quantity)}</td>
                    <td className="text-end cp-num">{formatReportUnitPrice(item.unit_price)}</td>
                    <td className="text-end cp-num fw-semibold">{formatReportMoney(item.line_amount)}</td>
                  </tr>
                );
              });

              return [
                ...medRows,
                <tr key={`${rx.dispensation_id}-total`} className="cp-rx-report-table__row is-rx-total is-rx-end">
                  <td className="cp-rx-report-table__total-label" colSpan={3}>
                    {copy.RX_TOTAL_LABEL}
                  </td>
                  <td className="text-end cp-num cp-rx-report-table__total-value">
                    <strong>{formatReportMoney(rx.total_amount)}</strong>
                  </td>
                </tr>
              ];
            })}
          </tbody>
        </Table>
      </div>

      <div className="cp-rx-report__footer">
        <div className="cp-rx-report__footer-meta">
          {copy.PAGE_RANGE.replace('{from}', String(rangeFrom))
            .replace('{to}', String(rangeTo))
            .replace('{total}', String(groups.length))}
        </div>
        <div className="cp-rx-report__footer-actions">
          <button
            type="button"
            className="cp-btn cp-btn--ghost cp-btn--sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label={copy.PAGE_PREV}
          >
            <Icon name="chevronLeft" size={14} aria-hidden />
            {copy.PAGE_PREV}
          </button>
          <span className="cp-rx-report__page-indicator">
            {copy.PAGE_OF.replace('{page}', String(page)).replace('{pages}', String(totalPages))}
          </span>
          <button
            type="button"
            className="cp-btn cp-btn--ghost cp-btn--sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            aria-label={copy.PAGE_NEXT}
          >
            {copy.PAGE_NEXT}
            <Icon name="chevronRight" size={14} aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}
